import { Moon, Sun } from 'lucide-react';
import { IconButton } from '@/components/shared/IconButton';
import { NotificationsPopover } from '@/components/shared/NotificationsSheet';
import { UploadTrayButton } from '@/components/shared/UploadTray';
import { useTranslator } from '@/hooks/useTranslator';
import { useActivityFeedStore } from '@/store/activityFeedStore';
import { useAgenticStore } from '@/store/agenticStore';
import { useSidebarStore } from '@/store/sidebarStore';
import { useThemeStore } from '@/store/themeStore';
import { AiSparkleIcon } from './AiSparkleIcon';
import { Breadcrumbs } from './Breadcrumbs';
import type { AppHeaderProps, BreadcrumbTrailItem } from './types';
import UserDropdown from './UserDropdown';

export type { BreadcrumbTrailItem };

export default function AppHeader({
  breadcrumb,
  breadcrumbTrail,
}: AppHeaderProps) {
  const setMobileOpen = useSidebarStore(s => s.setMobileOpen);
  const { theme, setThemeAnimated } = useThemeStore();
  const dark =
    theme === 'dark' ||
    (theme === 'system' &&
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches);
  const { open: notificationsOpen, setOpen: setNotificationsOpen } =
    useActivityFeedStore();
  const toggleAgentic = useAgenticStore(s => s.toggle);
  const agenticOpen = useAgenticStore(s => s.isOpen);
  const isStreaming = useAgenticStore(s => s.isStreaming);
  const isProcessing = useAgenticStore(s => s.isProcessing);
  const isClassifying = useAgenticStore(s => s.isClassifying);
  const processingPercentage = useAgenticStore(s => s.processingPercentage);
  const isAnimated = isStreaming || isProcessing || isClassifying;
  const { getTranslation } = useTranslator({ page: 'appHeader' });

  function toggleTheme(e: React.MouseEvent<HTMLButtonElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;
    setThemeAnimated(dark ? 'light' : 'dark', x, y);
  }

  return (
    <header className='col-start-2 flex items-center justify-between px-3 sm:px-4 sm:pr-5 border-b bg-header z-30 h-13'>
      {/* Left: brand + breadcrumb */}
      <div className='flex items-center gap-1 min-w-0'>
        <IconButton
          variant='ghost'
          size='icon'
          className='md:hidden h-7.5 w-7.5 text-muted-foreground mr-1'
          tooltip={getTranslation('openMenu')}
          onClick={() => setMobileOpen(true)}
        >
          <i className='fa-light fa-bars text-sm' />
        </IconButton>
        <div className='hidden sm:flex items-center gap-1'>
          <IconButton
            variant='ghost'
            size='icon'
            className='h-6.5 w-6.5'
            tooltip={getTranslation('back')}
            onClick={() => history.back()}
          >
            <i className='fa-light fa-chevron-left text-xs' />
          </IconButton>
          <IconButton
            variant='ghost'
            size='icon'
            className='h-6.5 w-6.5'
            tooltip={getTranslation('forward')}
            onClick={() => history.forward()}
          >
            <i className='fa-light fa-chevron-right text-xs' />
          </IconButton>
        </div>

        <div className='hidden md:flex min-w-0'>
          <Breadcrumbs
            breadcrumb={breadcrumb}
            breadcrumbTrail={breadcrumbTrail}
          />
        </div>
      </div>

      {/* Right: actions + user dropdown */}
      <div className='flex items-center gap-1.5 shrink-0'>
        {/* AI assistant + notifications — hidden on mobile */}
        <div className='flex items-center gap-1.5'>
          <button
            type='button'
            title={getTranslation('aiAssistant')}
            onClick={toggleAgentic}
            className={`flex items-center gap-1.5 rounded-md px-1.5 h-7.5 transition-colors hover:bg-accent ${isAnimated ? '[color:var(--color-ai)]' : agenticOpen ? 'text-foreground' : 'text-muted-foreground'}`}
          >
            <AiSparkleIcon streaming={isAnimated} />
            {isProcessing && !isStreaming && (
              <span className='text-xs font-semibold tabular-nums leading-none'>
                {processingPercentage}%
              </span>
            )}
          </button>

          <UploadTrayButton tooltip={getTranslation('uploads')} />

          <NotificationsPopover
            open={notificationsOpen}
            onOpenChange={setNotificationsOpen}
            trigger={
              <IconButton
                variant='ghost'
                size='icon'
                className='relative h-7.5 w-7.5 text-muted-foreground'
                tooltip={getTranslation('notifications')}
              >
                <i className='fa-light fa-bell text-sm' />
              </IconButton>
            }
          />
        </div>

        {/* Help — always visible */}
        <IconButton
          variant='ghost'
          size='icon'
          className='h-7.5 w-7.5 text-muted-foreground'
          tooltip={getTranslation('help')}
        >
          <i className='fa-light fa-circle-question text-sm' />
        </IconButton>

        <IconButton
          variant='ghost'
          size='icon'
          className='h-7.5 w-7.5 text-muted-foreground'
          onClick={toggleTheme}
          tooltip={getTranslation('toggleTheme')}
        >
          {dark ? <Moon size={14} /> : <Sun size={14} />}
        </IconButton>

        <div className='w-px h-4.5 bg-border mx-1' />

        <UserDropdown />
      </div>
    </header>
  );
}
